import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

// Images from public folder
const vslThumbOriginal = "/assets/imgs/vsl-thumb-original.jpg";
const heroVslCreator = "/assets/imgs/hero-vsl-creator.webp";
const vslThumbVariant = "/assets/imgs/vsl-thumb-original.jpg"; // Using same image for now

interface FunnelSceneProps {
  isActive: boolean;
}

type AnimationPhase = 
  | "idle"
  | "building"
  | "connecting"
  | "click-a"
  | "highlight-a"
  | "click-b"
  | "highlight-b"
  | "stable";

// Cursor component
function FakeCursor({ 
  position, 
  visible, 
  clicking 
}: { 
  position: { x: number; y: number }; 
  visible: boolean;
  clicking?: boolean;
}) {
  useLanguage();
  return (
    <div
      className={cn(
        "absolute z-50 pointer-events-none transition-all duration-300 ease-out",
        visible ? "opacity-100" : "opacity-0"
      )}
      style={{
        left: position.x,
        top: position.y,
        transform: clicking ? "scale(0.9)" : "scale(1)",
      }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        className="drop-shadow-md"
      >
        <path
          d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 0 1 .35-.15h6.87a.5.5 0 0 0 .35-.85L6.35 2.86a.5.5 0 0 0-.85.35Z"
          fill="#1F6BFF"
          stroke="white"
          strokeWidth="1.5"
        />
      </svg>
    </div>
  );
}

// Funnel Node component
interface FunnelNodeProps {
  title: string;
  imageSrc: string;
  question?: string;
  buttons?: { label: string; id: "a" | "b" }[];
  continueButton?: boolean;
  label?: string;
  highlighted?: boolean;
  showBadge?: boolean;
  visible: boolean;
  position: { x: string; y: string };
  highlightedButton?: "a" | "b" | null;
}

function FunnelNode({
  title,
  imageSrc,
  question,
  buttons,
  continueButton,
  label,
  highlighted,
  showBadge,
  visible,
  position,
  highlightedButton,
}: FunnelNodeProps) {
  useLanguage();
  return (
    <div
      className={cn(
        "absolute bg-white rounded-2xl border transition-all duration-300",
        highlighted
          ? "border-[#1F6BFF] border-[3px] shadow-[0_0_30px_rgba(31,107,255,0.25)] scale-[1.02] z-10"
          : "border-gray-200 shadow-[0_10px_30px_rgba(0,0,0,0.08)]",
        visible ? "opacity-100 scale-100" : "opacity-0 scale-95"
      )}
      style={{ 
        left: position.x, 
        top: position.y,
        width: buttons ? "115px" : "95px",
        transform: "translate(-50%, -50%)"
      }}
    >
      {/* Title bar */}
      <div className="px-1.5 py-0.5 border-b border-gray-100 flex items-center justify-between">
        <span className="text-[8px] font-medium text-gray-700 truncate">
          {title}
        </span>
        <button className="text-gray-400 hover:text-gray-600 text-[9px]">
          ×
        </button>
      </div>

      {/* Thumb */}
      <div className="relative aspect-video mx-1 mt-1 rounded-md overflow-hidden">
        <img
          src={imageSrc}
          alt={title}
          className="w-full h-full object-cover"
        />
        {showBadge && (
          <div className="absolute bottom-0.5 left-0.5 px-1 py-0.5 bg-[#1F6BFF]/90 rounded text-[6px] text-white font-medium animate-pulse">
            {translateInterfaceText("Reproduzindo...")}</div>
        )}
      </div>

      {/* Label (for response nodes) */}
      {label && (
        <div className="px-1.5 py-0.5">
          <span className="text-[7px] text-gray-400 uppercase tracking-wide">
            {label}
          </span>
        </div>
      )}

      {/* Question */}
      {question && (
        <div className="px-1.5 py-0.5">
          <p className="text-[7px] text-gray-500 leading-tight">{question}</p>
        </div>
      )}

      {/* Buttons A/B */}
      {buttons && (
        <div className="px-1 pb-1 space-y-0.5">
          {buttons.map((btn) => (
            <div
              key={btn.id}
              className={cn(
                "flex items-center gap-0.5 px-1 py-0.5 rounded border transition-all duration-200",
                highlightedButton === btn.id
                  ? "border-[#1F6BFF] bg-[#1F6BFF]/5"
                  : "border-gray-200 bg-gray-50"
              )}
            >
              <span
                className={cn(
                  "w-3 h-3 rounded-full text-[6px] font-bold flex items-center justify-center text-white flex-shrink-0",
                  btn.id === "a" ? "bg-[#1F6BFF]" : "bg-[#1F6BFF]/70"
                )}
              >
                {btn.id.toUpperCase()}
              </span>
              <span className="text-[6px] text-gray-600 truncate">
                {btn.label}
              </span>
            </div>
          ))}
          {/* Add button placeholder */}
          <div className="flex items-center justify-center py-0.5 rounded border border-dashed border-gray-200 text-[7px] text-gray-400">
            +
          </div>
        </div>
      )}

      {/* Continue button */}
      {continueButton && (
        <div className="px-1 pb-1">
          <div className="flex items-center gap-0.5 px-1 py-0.5 rounded border border-gray-200 bg-gray-50">
            <span className="w-3 h-3 rounded-full bg-[#1F6BFF] text-[6px] font-bold flex items-center justify-center text-white flex-shrink-0">
              A
            </span>
            <span className="text-[6px] text-gray-600">{translateInterfaceText("Continuar")}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Connector component
interface ConnectorProps {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  active?: boolean;
  visible?: boolean;
  dimmed?: boolean;
}

function Connector({ fromX, fromY, toX, toY, active, visible, dimmed }: ConnectorProps) {
  useLanguage();
  const pathRef = useRef<SVGPathElement>(null);
  const [pathLength, setPathLength] = useState(0);

  // Calculate bezier curve control points
  const midX = (fromX + toX) / 2;
  const d = `M ${fromX} ${fromY} C ${midX} ${fromY}, ${midX} ${toY}, ${toX} ${toY}`;

  useEffect(() => {
    if (pathRef.current) {
      setPathLength(pathRef.current.getTotalLength());
    }
  }, [d]);

  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible">
      <path
        ref={pathRef}
        d={d}
        fill="none"
        stroke="#1F6BFF"
        strokeWidth={active ? "3" : "2"}
        strokeDasharray="6 4"
        strokeLinecap="round"
        className={cn(
          "transition-all duration-300",
          active && "animate-connector-glow",
          dimmed && "opacity-30"
        )}
        style={{
          strokeDashoffset: visible ? 0 : pathLength,
          transition: visible ? "stroke-dashoffset 500ms ease-out, stroke-width 300ms, opacity 300ms" : "none",
        }}
      />
      {/* Connection point */}
      <circle
        cx={toX}
        cy={toY}
        r={active ? "5" : "4"}
        fill={active ? "#1F6BFF" : "#94A3B8"}
        stroke="white"
        strokeWidth="2"
        className={cn(
          "transition-all duration-300",
          visible ? "opacity-100" : "opacity-0",
          dimmed && "opacity-30"
        )}
      />
    </svg>
  );
}

// Mini sidebar component
function MiniSidebar({ visible }: { visible: boolean }) {
  useLanguage();
  return (
    <div
      className={cn(
        "absolute left-2 top-1/2 -translate-y-1/2 bg-white rounded-lg shadow-sm border border-gray-200 p-1.5 transition-all duration-300",
        visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"
      )}
    >
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1 px-1.5 py-1 rounded bg-gray-50">
          <svg
            className="w-3 h-3 text-gray-500"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="2" y="2" width="20" height="20" rx="2" />
            <polygon points="10,8 16,12 10,16" fill="currentColor" />
          </svg>
          <span className="text-[8px] text-gray-600 font-medium">
            {translateInterfaceText("Vídeos (1)")}</span>
        </div>
        <button className="flex items-center justify-center gap-0.5 px-1.5 py-1 rounded border border-dashed border-[#1F6BFF]/40 text-[8px] text-[#1F6BFF] hover:bg-[#1F6BFF]/5 transition-colors">
          <span>+</span>
          <span>{translateInterfaceText("Adicionar")}</span>
        </button>
      </div>
    </div>
  );
}

export function FunnelScene({ isActive }: FunnelSceneProps) {
  useLanguage();
  const [phase, setPhase] = useState<AnimationPhase>("idle");
  const [nodesVisible, setNodesVisible] = useState({ main: false, a: false, b: false });
  const [connectorsVisible, setConnectorsVisible] = useState({ a: false, b: false });
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [cursorVisible, setCursorVisible] = useState(false);
  const [cursorClicking, setCursorClicking] = useState(false);
  const [highlightedPath, setHighlightedPath] = useState<"a" | "b" | null>(null);
  const [highlightedButton, setHighlightedButton] = useState<"a" | "b" | null>(null);
  const [showBadge, setShowBadge] = useState<"a" | "b" | null>(null);

  // Node positions (percentages)
  const NODE_POSITIONS = {
    main: { x: "26%", y: "50%" },
    a: { x: "78%", y: "22%" },
    b: { x: "78%", y: "68%" },
  };

  // Connector pixel positions (calculated from container)
  const CONNECTOR_COORDS = {
    a: { fromX: 100, fromY: 155, toX: 285, toY: 70 },
    b: { fromX: 100, fromY: 195, toX: 285, toY: 240 },
  };

  // Cursor positions
  const CURSOR_POSITIONS = {
    start: { x: 150, y: 170 },
    buttonA: { x: 85, y: 178 },
    buttonB: { x: 85, y: 198 },
    hidden: { x: 200, y: 300 },
  };

  // Reset state when scene becomes inactive
  useEffect(() => {
    if (!isActive) {
      setPhase("idle");
      setNodesVisible({ main: false, a: false, b: false });
      setConnectorsVisible({ a: false, b: false });
      setSidebarVisible(false);
      setCursorVisible(false);
      setCursorClicking(false);
      setHighlightedPath(null);
      setHighlightedButton(null);
      setShowBadge(null);
    }
  }, [isActive]);

  // Animation timeline
  useEffect(() => {
    if (!isActive) return;

    const timers: NodeJS.Timeout[] = [];

    // Phase: building - Canvas appears
    timers.push(setTimeout(() => setPhase("building"), 0));
    timers.push(setTimeout(() => setNodesVisible((prev) => ({ ...prev, main: true })), 200));

    // Phase: connecting - Lines draw
    timers.push(setTimeout(() => {
      setPhase("connecting");
      setConnectorsVisible((prev) => ({ ...prev, a: true }));
    }, 500));
    timers.push(setTimeout(() => setNodesVisible((prev) => ({ ...prev, a: true })), 900));
    timers.push(setTimeout(() => setConnectorsVisible((prev) => ({ ...prev, b: true })), 1100));
    timers.push(setTimeout(() => setNodesVisible((prev) => ({ ...prev, b: true })), 1500));
    timers.push(setTimeout(() => setSidebarVisible(true), 1800));

    // Phase: click-a - Cursor appears and clicks button A
    timers.push(setTimeout(() => {
      setPhase("click-a");
      setCursorPosition(CURSOR_POSITIONS.start);
      setCursorVisible(true);
    }, 2000));
    timers.push(setTimeout(() => setCursorPosition(CURSOR_POSITIONS.buttonA), 2200));
    timers.push(setTimeout(() => {
      setCursorClicking(true);
      setHighlightedButton("a");
    }, 2400));
    timers.push(setTimeout(() => setCursorClicking(false), 2500));

    // Phase: highlight-a - Path A glows
    timers.push(setTimeout(() => {
      setPhase("highlight-a");
      setHighlightedPath("a");
      setShowBadge("a");
    }, 2600));

    // Phase: click-b - Cursor moves to button B
    timers.push(setTimeout(() => {
      setPhase("click-b");
      setCursorPosition(CURSOR_POSITIONS.buttonB);
    }, 3800));
    timers.push(setTimeout(() => {
      setCursorClicking(true);
      setHighlightedButton("b");
    }, 4200));
    timers.push(setTimeout(() => setCursorClicking(false), 4300));

    // Phase: highlight-b - Path B glows
    timers.push(setTimeout(() => {
      setPhase("highlight-b");
      setHighlightedPath("b");
      setShowBadge("b");
    }, 4400));

    // Phase: stable - Cursor disappears
    timers.push(setTimeout(() => {
      setPhase("stable");
      setCursorVisible(false);
    }, 5500));

    return () => timers.forEach(clearTimeout);
  }, [isActive]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-[13px] font-semibold text-gray-800">
              {translateInterfaceText("Funil de Vídeo")}</h3>
            <p className="text-[10px] text-gray-500 mt-0.5 max-w-[280px]">
              {translateInterfaceText("Crie jornadas interativas com botões que levam a vídeos diferentes.")}</p>
          </div>
        </div>
      </div>

      {/* Canvas area */}
      <div className="flex-1 relative overflow-hidden">
        {/* Dotted grid background */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `radial-gradient(circle, #D1D5DB 1px, transparent 1px)`,
            backgroundSize: "16px 16px",
          }}
        />

        {/* Mini sidebar */}
        <MiniSidebar visible={sidebarVisible} />

        {/* Connectors */}
        <Connector
          fromX={CONNECTOR_COORDS.a.fromX}
          fromY={CONNECTOR_COORDS.a.fromY}
          toX={CONNECTOR_COORDS.a.toX}
          toY={CONNECTOR_COORDS.a.toY}
          visible={connectorsVisible.a}
          active={highlightedPath === "a"}
          dimmed={highlightedPath === "b"}
        />
        <Connector
          fromX={CONNECTOR_COORDS.b.fromX}
          fromY={CONNECTOR_COORDS.b.fromY}
          toX={CONNECTOR_COORDS.b.toX}
          toY={CONNECTOR_COORDS.b.toY}
          visible={connectorsVisible.b}
          active={highlightedPath === "b"}
          dimmed={highlightedPath === "a"}
        />

        {/* Nodes */}
        <FunnelNode
          title={translateInterfaceText("VSL Principal")}
          imageSrc={vslThumbOriginal}
          question={translateInterfaceText("Qual opção combina mais com você?")}
          buttons={[
            { label: translateInterfaceText("Plano mais rápido"), id: "a" },
            { label: translateInterfaceText("Mais detalhes"), id: "b" },
          ]}
          visible={nodesVisible.main}
          position={NODE_POSITIONS.main}
          highlightedButton={highlightedButton}
        />

        <FunnelNode
          title={translateInterfaceText("VSL Direcionada A")}
          imageSrc={heroVslCreator}
          label={translateInterfaceText("Resposta A")}
          continueButton
          visible={nodesVisible.a}
          position={NODE_POSITIONS.a}
          highlighted={highlightedPath === "a"}
          showBadge={showBadge === "a"}
        />

        <FunnelNode
          title={translateInterfaceText("VSL Direcionada B")}
          imageSrc={vslThumbVariant}
          label={translateInterfaceText("Resposta B")}
          continueButton
          visible={nodesVisible.b}
          position={NODE_POSITIONS.b}
          highlighted={highlightedPath === "b"}
          showBadge={showBadge === "b"}
        />

        {/* Fake cursor */}
        <FakeCursor
          position={cursorPosition}
          visible={cursorVisible}
          clicking={cursorClicking}
        />
      </div>
    </div>
  );
}
